// KPI set / gösterge / işletme / etiket / kanıt şemaları — schemas/kpi.ts 600 satır bölmesi (TODO-KPI-114/-144, salt taşıma); `kpi.ts` barrel'i yeniden dışa açar.
import { z } from "zod";
import {
  CLIENT_ENTITY_TYPES,
  EVIDENCE_RETENTION_POLICY_RE,
  EVIDENCE_SCAN_STATUSES,
  EVIDENCE_SOURCE_TYPES,
  KPI_AGGREGATIONS,
  KPI_CLIENT_ACCESS,
  KPI_EFFECTIVE_CLIENT_ACCESS,
  KPI_NODE_KINDS,
  KPI_REPORT_SCOPES,
  KPI_REPORT_TABLE_LAYOUTS,
  KPI_VALUE_TYPES,
  KpiBreakdownSchema,
} from "./kpi-constants.js";
import { KpiFormulaListSchema } from "./kpi-metrics.js";

// ---- Set (kpi_sets) ----
// TODO-KPI-29 (2026-10-07): setin kapsamı `frameworks` tablosundan seçilir (`frameworkId`;
// null = "Çerçevesiz / Diğer"). `reportScope` geriye uyum için kalır — FE göstermez, API'de
// opsiyonel (eski üç etiket), varsayılan "gri".
export const KpiSetSchema = z.object({
  id: z.string().uuid(),
  clientId: z.string().uuid(),
  name: z.string(),
  frameworkId: z.string().uuid().nullable(),
  /** Çerçeve adı (join) — çerçeve soft-delete edildiyse de dolu gelir; null = çerçevesiz. */
  frameworkName: z.string().nullable(),
  reportScope: z.enum(KPI_REPORT_SCOPES),
  reportTableLayout: z.enum(KPI_REPORT_TABLE_LAYOUTS),
  createdBy: z.string().uuid().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
  /** M17 T5 — yalnız getSet doldurur ve yalnız ≠1 (mali yıl) iken; takvim müşterisinde alan yok. */
  fiscalYearStartMonth: z.number().int().min(1).max(12).optional(),
  /**
   * Kırılım (0069, §6.7): DOCX'te kırılım grupları/üyeleri gösterilir mi. Yalnız `false` iken yazılır
   * (alan yokluğu = açık, varsayılan) — eski set DTO'ları byte-eşit.
   */
  reportBreakdowns: z.boolean().optional(),
});
export type KpiSet = z.infer<typeof KpiSetSchema>;

// Fork girdisi: katalogdan seçilen metrikler (+ zorunlu atalar sunucuda eklenir).
export const KpiSetCreateSchema = z.object({
  clientId: z.string().uuid(),
  name: z.string().min(1).max(200),
  frameworkId: z.string().uuid().nullable().optional(),
  reportScope: z.enum(KPI_REPORT_SCOPES).optional(),
  reportTableLayout: z.enum(KPI_REPORT_TABLE_LAYOUTS).optional(),
  includeMetricIds: z.array(z.string().uuid()).min(1),
  /** Sektör paketleri: yalnız iz/varsayılan/deprecated denetimi (FE ön seçimi); üyelik yalnız `includeMetricIds` (DR1). */
  packageIds: z.array(z.string().uuid()).max(50).optional(),
  /** Birim varyantı: fork anında metrik başına seçilen birim (müşterinin kalıcı tercihi). */
  unitPreferences: z
    .array(z.object({ metricId: z.string().uuid(), unit: z.string().max(32) }))
    .max(500)
    .optional(),
});
export type KpiSetCreate = z.infer<typeof KpiSetCreateSchema>;

export const KpiSetUpdateSchema = z.object({
  name: z.string().min(1).max(200).optional(),
  /** null → çerçeve bağı kaldırılır ("Çerçevesiz / Diğer"). */
  frameworkId: z.string().uuid().nullable().optional(),
  reportScope: z.enum(KPI_REPORT_SCOPES).optional(),
  reportTableLayout: z.enum(KPI_REPORT_TABLE_LAYOUTS).optional(),
  /** Kırılım (0069): "Raporda kırılımları göster" — false → DOCX'te yalnız toplam satırları. */
  reportBreakdowns: z.boolean().optional(),
});
export type KpiSetUpdate = z.infer<typeof KpiSetUpdateSchema>;

/** `GET /kpi/frameworks` — set oluşturma/ayarlarında seçilebilir yayınlanmış çerçeveler. */
export const KpiFrameworkOptionSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  /** Yayındaki sürüm numarası (ör. "v2"). */
  currentVersion: z.string().nullable(),
});
export type KpiFrameworkOption = z.infer<typeof KpiFrameworkOptionSchema>;

// ---- Set göstergesi (kpi_set_indicators ⋈ kpi_metrics — üyelik + override) ----
export const KpiSetIndicatorSchema = z.object({
  id: z.string().uuid(),
  setId: z.string().uuid(),
  metricId: z.string().uuid(),
  code: z.string(),
  parentId: z.string().uuid().nullable(),
  /** override ?? metrik etiketi */
  label: z.string(),
  labelOverride: z.string().nullable(),
  /**
   * Finar rev-B 3 — katalog metriğinin açıklaması (formüllü hücre hover kartı). Yalnız metrik
   * açıklaması doluysa yazılır; alan yokluğu = açıklama yok (eski DTO/JSON byte-eşit). YALNIZ İÇ
   * (danışman) DTO — portal yüzeyine çıkmaz (`stripInternalIndicatorFields`, rev-B B1).
   */
  description: z.string().nullable().optional(),
  /** Müşterinin kalıcı birimi ?? metrik varsayılan birimi */
  unit: z.string().nullable(),
  /** Birimin kaynağı: müşteri tercihi mi, katalog varsayılanı mı. */
  unitSource: z.enum(["client", "default"]),
  valueType: z.enum(KPI_VALUE_TYPES),
  isDerived: z.boolean(),
  formulas: KpiFormulaListSchema,
  activeFormulaId: z.string().uuid().nullable(),
  aggregation: z.enum(KPI_AGGREGATIONS),
  /** set bazlı konsolide kuralı override'ı; null = katalog varsayılanı (`aggregation` etkin değer) */
  aggregationOverride: z.enum(KPI_AGGREGATIONS).nullable(),
  /** TODO-KPI-32: etkin ağırlık göstergesi (metrik id) = override ?? katalog varsayılanı. */
  aggregationWeightMetricId: z.string().uuid().nullable(),
  /** set bazlı ağırlık override'ı; null = katalog varsayılanı */
  aggregationWeightOverride: z.string().uuid().nullable(),
  showChange: z.boolean(),
  increaseIsGood: z.boolean().nullable(),
  anomalyThreshold: z.number().nullable(),
  footnote: z.string().nullable(),
  clientAccess: z.enum(KPI_CLIENT_ACCESS),
  /** `inherit` zinciri çözülmüş hali (K12). */
  effectiveClientAccess: z.enum(KPI_EFFECTIVE_CLIENT_ACCESS),
  linkedQuestionCode: z.string().nullable(),
  orderIndex: z.number().int(),
  checkedBy: z.string().uuid().nullable(),
  checkedAt: z.string().nullable(),
  pendingFormulaId: z.string().uuid().nullable(),
  pendingRequestedBy: z.string().uuid().nullable(),
  pendingRequestedAt: z.string().nullable(),
  depth: z.number().int().nonnegative(),
  /** Kırılım hiyerarşisi: alan yokluğu = kırılımsız (eski DTO byte-eşit). */
  nodeKind: z.enum(KPI_NODE_KINDS).optional(),
  breakdown: KpiBreakdownSchema.nullable().optional(),
  dimensionMember: z.string().nullable().optional(),
  calculationGroupMetricId: z.string().uuid().nullable().optional(),
  /**
   * Kırılım (spec §3.3): resolve `isDerived`'i ETKİN değerle yazar (müşteri geneli hesap grubu);
   * katalogdaki ham değer yalnız ikisi farklıyken burada gelir (alan yokluğu = `isDerived` ile aynı).
   */
  catalogDerived: z.boolean().optional(),
  /**
   * Sektör paketleri (DR9 / IndicatorModal "Kaynak: <paket> paketi"): satırı sete getiren paket.
   * Yalnız doluyken yazılır (alan yokluğu = paketsiz; eski DTO byte-eşit). YALNIZ İÇ DTO.
   */
  sourcePackageId: z.string().uuid().nullable().optional(),
});
export type KpiSetIndicator = z.infer<typeof KpiSetIndicatorSchema>;

export const KpiSetIndicatorPatchSchema = z.object({
  labelOverride: z.string().max(200).nullable().optional(),
  unit: z.string().max(32).nullable().optional(),
  unitMode: z.enum(["convert", "relabel"]).optional(),
  aggregationOverride: z.enum(KPI_AGGREGATIONS).nullable().optional(),
  aggregationWeightOverride: z.string().uuid().nullable().optional(),
  formulas: KpiFormulaListSchema.optional(),
  activeFormulaId: z.string().uuid().nullable().optional(),
  clientAccess: z.enum(KPI_CLIENT_ACCESS).optional(),
  linkedQuestionCode: z.string().max(64).nullable().optional(),
  showChange: z.boolean().optional(),
  increaseIsGood: z.boolean().nullable().optional(),
  anomalyThreshold: z.number().nullable().optional(),
  footnote: z.string().max(2000).nullable().optional(),
  parentId: z.string().uuid().nullable().optional(),
  orderIndex: z.number().int().optional(),
  /** 0069 (D7/D9) — açık hesap grubu (toplamın set satırında); null = ilk tam grup. */
  calculationGroupMetricId: z.string().uuid().nullable().optional(),
});
export type KpiSetIndicatorPatch = z.infer<typeof KpiSetIndicatorPatchSchema>;

// ---- Müşteri işletmesi (client_entities — K27 işletme ağacı) ----
export const ClientEntitySchema = z.object({
  id: z.string().uuid(),
  clientId: z.string().uuid(),
  parentId: z.string().uuid().nullable(),
  type: z.enum(CLIENT_ENTITY_TYPES),
  name: z.string(),
  code: z.string(),
  country: z.string().nullable(),
  isDefault: z.boolean(),
  isActive: z.boolean(),
  /**
   * TODO-KPI-11 — gruba/konsolideye dahil olduğu yıl aralığı (dahil, yıl). null/yok = sınırsız.
   * Sunucu DTO'su her zaman doldurur; opsiyonel bırakılması eski fixture'ların kırılmaması için.
   */
  validFrom: z.number().int().nullable().optional(),
  validTo: z.number().int().nullable().optional(),
  orderIndex: z.number().int(),
  createdAt: z.string(),
  updatedAt: z.string(),
  /** Yalnız `GET /clients/:id/entities` listesinde: işletmenin `kpi_values` satır sayısı (tur P2-7). */
  valueCount: z.number().int().nonnegative().optional(),
});
export type ClientEntity = z.infer<typeof ClientEntitySchema>;

const EntityYear = z.number().int().min(1990).max(2100);
export const ClientEntityCreateSchema = z.object({
  name: z.string().min(1).max(200),
  type: z.enum(CLIENT_ENTITY_TYPES).optional(),
  parentId: z.string().uuid().nullable().optional(),
  country: z.string().length(2).nullable().optional(),
  /** Dahil olduğu ilk yıl (null = sınırsız). Servis `validFrom <= validTo` doğrular. */
  validFrom: EntityYear.nullable().optional(),
  /** Dahil olduğu son yıl (null = sınırsız). */
  validTo: EntityYear.nullable().optional(),
});
export type ClientEntityCreate = z.infer<typeof ClientEntityCreateSchema>;

export const ClientEntityUpdateSchema = ClientEntityCreateSchema.partial().extend({
  isActive: z.boolean().optional(),
  orderIndex: z.number().int().optional(),
});
export type ClientEntityUpdate = z.infer<typeof ClientEntityUpdateSchema>;

// ---- Gösterge etiketleri — çok dillilik (kpi_metric_labels — TODO-KPI-09) ----
// Katalog etiketi (`kpi_metrics.label`) Türkçe ve tek kaynaktır; diğer diller bu tabloda durur.
// `resolveSetValues(setId, { lang })`: `lang` verilirse gösterge etiketi tablodan çevrilir
// (çeviri yoksa katalog etiketi), verilmezse (ya da "tr") çıktı byte-eşit kalır.
export const KPI_LANGS = ["tr", "en"] as const;
export type KpiLang = (typeof KPI_LANGS)[number];
export const KPI_LANG_LABELS: Record<KpiLang, string> = { tr: "Türkçe", en: "İngilizce" };
/** Varsayılan dil — katalog etiketinin dili; etiket tablosunda satırı olmaz. */
export const KPI_DEFAULT_LANG: KpiLang = "tr";
export const KpiLangSchema = z.enum(KPI_LANGS);

export const KpiMetricLabelSchema = z.object({
  metricId: z.string().uuid(),
  lang: KpiLangSchema,
  label: z.string(),
  description: z.string().nullable(),
});
export type KpiMetricLabel = z.infer<typeof KpiMetricLabelSchema>;

/** PUT /kpi/metrics/:id/labels gövdesi — `lang` varsayılan dil olamaz (katalog etiketi kaynaktır). */
export const KpiMetricLabelUpsertSchema = z.object({
  lang: KpiLangSchema.refine((l) => l !== KPI_DEFAULT_LANG, {
    message: "Varsayılan dil (tr) etiketi katalogda düzenlenir",
  }),
  label: z.string().trim().min(1).max(200),
  description: z.string().trim().max(2000).nullable().optional(),
});
export type KpiMetricLabelUpsert = z.infer<typeof KpiMetricLabelUpsertSchema>;

// ---- Kanıt belgesi (evidence_documents — K17) ----
/**
 * TODO-EVIDENCE-01 — dosyanın bağlı olduğu form soruları (`form_response_evidence`). KPI kanıt
 * panelinde "form kanıtı · <soru kodu>" rozeti; aynı dosya KPI hücresine de bağlanabilir.
 */
export const EvidenceFormLinkSchema = z.object({
  projectId: z.string().uuid(),
  questionCode: z.string(),
  reportingYear: z.number().int().nullable(),
});
export type EvidenceFormLink = z.infer<typeof EvidenceFormLinkSchema>;

export const EvidenceDocumentSchema = z.object({
  id: z.string().uuid(),
  clientId: z.string().uuid(),
  storagePath: z.string(),
  fileName: z.string(),
  mimeType: z.string().nullable(),
  sizeBytes: z.number().int().nullable(),
  checksum: z.string().nullable(),
  scanStatus: z.enum(EVIDENCE_SCAN_STATUSES),
  sourceType: z.enum(EVIDENCE_SOURCE_TYPES),
  confidentiality: z.string().nullable(),
  retentionPolicy: z.string().nullable(),
  uploadedBy: z.string().uuid().nullable(),
  uploadedAt: z.string(),
  // TODO-EVIDENCE-02 — retention temizliği nesneyi sildiyse (import kaynağı: satır + bağlar kalır).
  purgedAt: z.string().nullable().optional(),
  // Son tarama hatası (pending kalan satırda neden; cron yeniden dener).
  scanError: z.string().nullable().optional(),
  // İndirilebilirlik = scanStatus === "clean" && !purgedAt (backend `isEvidenceDownloadable`;
  // portal `downloadable` alanını erişim politikasıyla ayrıca hesaplar).
  // TODO-EVIDENCE-01 — form soru bağları (müşteri havuzu listesi + hücre kanıt listesi doldurur).
  formLinks: z.array(EvidenceFormLinkSchema).optional(),
});
export type EvidenceDocument = z.infer<typeof EvidenceDocumentSchema>;

/** PATCH /kpi/evidence/:id — dosya bazlı saklama politikası (TODO-EVIDENCE-03). */
export const UpdateEvidenceRetentionRequestSchema = z.object({
  retentionPolicy: z
    .string()
    .trim()
    .toLowerCase()
    .regex(EVIDENCE_RETENTION_POLICY_RE, "system | keep | <n>d biçiminde olmalı"),
});
export type UpdateEvidenceRetentionRequest = z.infer<typeof UpdateEvidenceRetentionRequestSchema>;

/** Kanıt içi konum (kpi_value_evidence.locator — E7 tek yer): Excel hücresi ya da PDF sayfası. */
export const KpiEvidenceLocatorSchema = z.union([
  z.object({ sheet: z.string(), cell: z.string() }),
  z.object({
    page: z.number().int().positive(),
    row: z.number().int().optional(),
    snippet: z.string().optional(),
  }),
]);
export type KpiEvidenceLocator = z.infer<typeof KpiEvidenceLocatorSchema>;
